/**
 * participantId — stable per-device identity for voting and similar
 * lightweight personalisation. Used by PIN-authenticated workshop devices
 * (Samsung touchscreens, iPads) that don't have an Entra session.
 *
 * The backend prefers an Entra session cookie when present, so generating
 * this id for everyone is harmless — Entra voters will still vote as their
 * email, not as 'participant:<uuid>'. UUID v4 is generated on first read
 * and persisted to localStorage so refresh + new tabs keep the same vote.
 *
 * Note: this is intentionally low-bar. It is NOT authentication. It is a
 * stable nickname for voting on this device.
 */

function generateUuidV4() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  // Fallback for crypto-less environments (very old WebView). Less random
  // but sufficient — voting collisions are negligible at workshop scale.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getOrCreateParticipantId() {
  if (typeof window === 'undefined') return null;
  let id = null;
  try {
    id = window.localStorage.getItem('participantId');
  } catch { /* private mode etc. — fall through */ }
  if (id) return id;
  const fresh = generateUuidV4();
  try {
    window.localStorage.setItem('participantId', fresh);
  } catch { /* storage disabled — caller can still use the in-memory id */ }
  return fresh;
}
